import { Button, createTableColumn, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components'
import { MoreVertical20Regular } from '@fluentui/react-icons'
import { t } from '../i18n/i18n'
import { Access, HttpResponse, OrganisationWithUserAccountsDTO, Role, SandboxOrganisationDTO, UserAccount } from '../api/schemas/schema'
import { IDataContext } from '../interfaces/IDataContext'
import { setLoading, handleMessage } from './stateHelper'
import { api } from './apiHelper'
import { AccountInfo } from '@azure/msal-browser'
import { UseFormClearErrors, UseFormReset, UseFormSetError } from 'react-hook-form'
import { IModalState } from '../interfaces/IModalState'
import { downloadOrganisationContract } from './documentHelper'
import { createUserUpdateEmailRequest } from './emailhelper'
import { sendSubscriptionUpdateNotification } from './emailSender'

export const getOrganisationColumns = (setSelectedOrganisation: React.Dispatch<React.SetStateAction<OrganisationWithUserAccountsDTO>>) => [
   createTableColumn<OrganisationWithUserAccountsDTO>({
      columnId: 'organisationNumber',
      compare: (a, b) => {
         return a.organisation.organisationNumber.localeCompare(b.organisation.organisationNumber)
      },
      renderHeaderCell: () => t('OrganisationNumber'),
      renderCell: (item) => {
         return `${item.organisation.organisationNumber.slice(0, 6)}-${item.organisation.organisationNumber.slice(6)}`
      },
   }),
   createTableColumn<OrganisationWithUserAccountsDTO>({
      columnId: 'name',
      compare: (a, b) => {
         return a.organisation.name.localeCompare(b.organisation.name)
      },
      renderHeaderCell: () => t('CompanyName'),
      renderCell: (item) => {
         return item.organisation?.name
      },
   }),
   createTableColumn<OrganisationWithUserAccountsDTO>({
      columnId: 'organisationSize',
      compare: (a, b) => {
         const valueA = a.organisation.organisationSize?.text || ''
         const valueB = b.organisation.organisationSize?.text || ''
         return valueA.localeCompare(valueB)
      },
      renderHeaderCell: () => t('OrganisationSize'),
      renderCell: (item) => {
         return item?.organisation?.organisationSize?.text || '-'
      },
   }),
   createTableColumn<OrganisationWithUserAccountsDTO>({
      columnId: 'organisationType',
      compare: (a, b) => {
         const valueA = a.organisation.organisationType?.text || ''
         const valueB = b.organisation.organisationType?.text || ''
         return valueA.localeCompare(valueB)
      },
      renderHeaderCell: () => t('OrganisationType'),
      renderCell: (item) => {
         return item?.organisation?.organisationType?.text || '-'
      },
   }),
   createTableColumn<OrganisationWithUserAccountsDTO>({
      columnId: 'partner',
      compare: (a, b) => {
         return a.organisation.isPartner.toString().localeCompare(b.organisation.isPartner.toString())
      },
      renderHeaderCell: () => t('Partner'),
      renderCell: (item) => {
         return item.organisation.isPartner ? t('Yes') : ''
      },
   }),
   createTableColumn<OrganisationWithUserAccountsDTO>({
      columnId: 'more',
      renderCell: (item) => {
         return (
            <Menu>
               <MenuTrigger>
                  <Button icon={<MoreVertical20Regular />} appearance="transparent" onClick={(e: any) => e.stopPropagation()} />
               </MenuTrigger>
               <MenuPopover>
                  <MenuList>
                     <MenuItem
                        onClick={(e) => {
                           e.stopPropagation()
                           setSelectedOrganisation(item)
                        }}
                     >
                        {t('Delete')}
                     </MenuItem>
                  </MenuList>
               </MenuPopover>
            </Menu>
         )
      },
   }),
]

export const getOrganisations = async (
   setOrganisations: React.Dispatch<React.SetStateAction<OrganisationWithUserAccountsDTO[]>>,
   dataContext: IDataContext,
   setFetchError?: React.Dispatch<React.SetStateAction<boolean>>,
   signal?: AbortSignal
) => {
   const { accessToken } = dataContext.state
   setLoading(dataContext.setRootState, true)
   try {
      const response = await api(accessToken).getCustomers({ signal })
      if (response?.data?.length > 0) {
         setOrganisations(response.data)
      }
      setFetchError?.(false)
   } catch (error: any) {
      if (error?.name !== 'AbortError') {
         handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchOrganisations'), '')
         setFetchError?.(true)
      }
   } finally {
      setLoading(dataContext.setRootState, false)
   }
}
const formatUserAccount = (userAccount: UserAccount, msalAccount: AccountInfo, organisationId?: string) => {
   const authUser = msalAccount.name
   return {
      ...userAccount,
      createdBy: userAccount.id ? userAccount.createdBy : authUser,
      modifiedBy: authUser,
      accesses: [] as Access[],
      organisationId: organisationId,
   }
}

const formatOrganisation = (form: OrganisationWithUserAccountsDTO, accounts: AccountInfo[]) => {
   const authUser = accounts[0]

   const modules = form?.organisation?.modules?.map((itm: any) => ({
      id: itm.id || null,
      isActivated: itm.isActivated,
      moduleType: itm.moduleType,
      demandComplianceModuleDefinitionId: itm.demandComplianceModuleDefinitionId || null,
      riskManagementModule: itm.riskManagementModule ?? null,
      component: itm.component ?? null,
      generalModule: itm.generalModule ?? null,
   }))

   const payload: OrganisationWithUserAccountsDTO = {
      ...form,
      organisation: {
         ...form.organisation,
         organisationProcessIds: form.organisation.organisationProcessIds ?? [],
         modules: modules,
         createdBy: form.organisation.id ? form.organisation.createdBy : authUser.name,
         modifiedBy: authUser.name,
         country: 'Sverige',
      },
      userAccounts: form.userAccounts.map((userAccount: UserAccount) => formatUserAccount(userAccount, authUser, form.organisation.id)),
      sandboxOrganisations: form.sandboxOrganisations?.map((sandbox) => ({
         ...sandbox,
         userAccount: sandbox.userAccount ? formatUserAccount(sandbox.userAccount, authUser) : null,
      })),
   }

   return payload
}

export const saveOrganisation = async (
   data: OrganisationWithUserAccountsDTO,
   reset: UseFormReset<OrganisationWithUserAccountsDTO>,
   dataContext: IDataContext,
   accounts: AccountInfo[],
   setModalState: React.Dispatch<React.SetStateAction<IModalState<OrganisationWithUserAccountsDTO>>>,
   setCloseOnSubmit: React.Dispatch<React.SetStateAction<boolean>>,
   previousData: OrganisationWithUserAccountsDTO,
   welcomeEmailSent: boolean,
   onClose?: () => void
) => {
   setLoading(dataContext.setRootState, true)
   const isCreated = data.organisation.id ? true : false
   const { accessToken } = dataContext.state
   try {
      let response: HttpResponse<OrganisationWithUserAccountsDTO, any>
      const formattedData = formatOrganisation(data, accounts)

      if (isCreated) {
         response = await api(accessToken).updateCustomer(formattedData)
         handleMessage(dataContext.setRootState, 'success', t('OrganisationUpdated'), '')

         // Uppdateringsmailet får aldrig bli kundens första mail. Det skickas därför först när
         // välkomstmailet bevisligen gått ut, vilket sker via knappen i kundformuläret.
         if (welcomeEmailSent) {
            sendSubscriptionUpdateNotification(
               dataContext,
               createUserUpdateEmailRequest(data, previousData, dataContext.state.demandComplianceModuleDefinitions, dataContext.state.processes)
            )
         }
      } else {
         response = await api(accessToken).addCustomer(formattedData)
         handleMessage(dataContext.setRootState, 'success', t('OrganisationCreated'), '')
         if (response.ok) {
            await downloadOrganisationContract(response.data.organisation, dataContext)
         }
      }
      if (response.ok) {
         if (onClose) {
            onClose()
         } else {
            setModalState((prev) => ({ ...prev, item: response.data }))
            reset(response.data)
         }
      }
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', isCreated ? t('CouldNotUpdateOrganisation') : t('CouldNotCreateOrganisation'), '')
   } finally {
      setCloseOnSubmit(false)
      setLoading(dataContext.setRootState, false)
   }
}

export const upgradeSandboxToOrganisation = async (
   sandbox: SandboxOrganisationDTO,
   dataContext: IDataContext,
   setModalState: React.Dispatch<React.SetStateAction<IModalState<OrganisationWithUserAccountsDTO>>>,
   reset: UseFormReset<OrganisationWithUserAccountsDTO>
) => {
   setLoading(dataContext.setRootState, true)

   const { accessToken } = dataContext.state
   try {
      sandbox.userAccount.accesses = []

      const organisation = (await api(accessToken).upgradeSandboxToOrganisation(sandbox)).data

      reset(organisation)

      setModalState({ isOpen: true, item: organisation })

      handleMessage(dataContext.setRootState, 'success', t('SandboxUpdated'), '')
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotUpdateSandbox'), '')
   } finally {
      setLoading(dataContext.setRootState, false)
   }
}

export const deleteOrganisation = async (
   organisationId: string,
   dataContext: IDataContext,
   setSelectedOrganisation: React.Dispatch<React.SetStateAction<OrganisationWithUserAccountsDTO>>,
   fetch: () => void
) => {
   setLoading(dataContext.setRootState, true)
   const { accessToken } = dataContext.state
   try {
      const response = await api(accessToken).deleteCustomer(organisationId)
      if (response.ok) {
         fetch()
         handleMessage(dataContext.setRootState, 'success', t('OrganisationDeleted'), '')
      }
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotDeleteOrganisation'), '')
   } finally {
      setSelectedOrganisation(null)
      setLoading(dataContext.setRootState, false)
   }
}
export const validateSystemadminRequirement = (
   userAccounts: UserAccount[],
   setError: UseFormSetError<OrganisationWithUserAccountsDTO>,
   clearErrors: UseFormClearErrors<OrganisationWithUserAccountsDTO>
) => {
   const hasNoUsers = !userAccounts || userAccounts.length === 0
   const hasAdmin = userAccounts?.some((ua) => ua.role === Role.SystemAdministrator)
   const noChoiceMade = userAccounts && userAccounts.some((ua) => ua.role === undefined)

   if (hasNoUsers || hasAdmin || noChoiceMade) {
      clearErrors('userAccounts')
   } else {
      setError('userAccounts', { message: t('AtleastOneSystemAdminRequired') })
   }
}
